import { text } from '../lib/format.js';

/**
 * 랜덤박스 경품 등급 뱃지.
 *
 * 같은 1~7·L 등급을 세 화면이 **저마다 다른 색표와 다른 마크업**으로 그리고
 * 있었다 - 주문상세는 이 파일의 인라인 그라디언트, 경품 관리는 CSS 클래스
 * (`randombox-grade--*`), 주문목록의 고객 당첨 이력은 또 다른 색표를 페이지
 * 안에 직접 들고 있었다.
 *
 * **색은 화면별로 그대로 유지한다** (2026-09-30 결정). 레거시가 화면마다 다른
 * 색을 쓰기 때문이다 - prize_list.php 의 getGradeGradient 와
 * randombox_order_customer.php 의 getGradeStyle 은 애초에 다른 배색이고, 경품
 * 관리의 CSS 색표는 밝은 테마 값까지 따로 맞춰 둔 것이다. 하나로 합치면 화면에
 * 보이던 색이 바뀐다.
 *
 * 합치는 것은 **마크업과 빈 값 처리**다. 어느 화면이든 `<span>` 한 겹이고,
 * L 은 'LUCK', 등급이 없으면 '-' 로 같게 나간다.
 */

/** 주문상세·정의 기반 리포트(Cell). 원본 prize_list.php 의 getGradeGradient. */
const ORDER_DETAIL_GRADIENT: Record<string, string> = {
  '1': 'linear-gradient(to right, #fbbf24, #f59e0b)',
  '2': 'linear-gradient(to right, #94a3b8, #64748b)',
  '3': 'linear-gradient(to right, #d97706, #b45309)',
  '4': 'linear-gradient(to right, #60a5fa, #2563eb)',
  '5': 'linear-gradient(to right, #34d399, #059669)',
  '6': 'linear-gradient(to right, #a78bfa, #7c3aed)',
  '7': 'linear-gradient(to right, #f87171, #dc2626)',
  L: 'linear-gradient(to right, #f472b6, #db2777)',
};
const ORDER_DETAIL_DEFAULT = 'linear-gradient(to right, #475569, #334155)';

/** 주문목록의 고객별 당첨 이력. 원본 randombox_order_customer.php 의 getGradeStyle 그대로. */
const CUSTOMER_STYLE: Record<string, { background: string; color: string }> = {
  '1': { background: 'linear-gradient(rgb(255, 215, 0), rgb(255, 247, 0))', color: 'black' },
  '2': { background: 'linear-gradient(rgb(192, 192, 192), rgb(232, 232, 232))', color: 'black' },
  '3': { background: 'linear-gradient(rgb(205, 127, 50), rgb(210, 105, 30))', color: 'black' },
  '4': { background: 'linear-gradient(rgb(65, 105, 225), rgb(100, 149, 237))', color: 'white' },
  '5': { background: 'linear-gradient(rgb(138, 43, 226), rgb(218, 112, 214))', color: 'white' },
  '6': { background: 'linear-gradient(rgb(50, 205, 50), rgb(144, 238, 144))', color: 'black' },
  '7': { background: 'linear-gradient(rgb(255, 99, 71), rgb(255, 160, 122))', color: 'black' },
  L: { background: 'linear-gradient(rgb(255, 20, 147), rgb(255, 105, 180))', color: 'white' },
};
const CUSTOMER_DEFAULT = {
  background: 'linear-gradient(rgb(105, 105, 105), rgb(169, 169, 169))',
  color: 'white',
};

/**
 * 어느 화면의 배색으로 그릴지.
 *
 * - `orderDetail` - 주문상세·정의 기반 리포트. 인라인 그라디언트.
 * - `customerHistory` - 주문목록의 고객별 당첨 이력. 레거시 배색.
 * - `prizeList` - 경품 관리. `styles.css` 의 `.randombox-grade--*` 를 쓴다.
 *   이 색표만 `light-dark()` 로 밝은 테마 값을 따로 갖고 있어 CSS 에 남긴다.
 */
export type GradePalette = 'orderDetail' | 'customerHistory' | 'prizeList';

/** 경품 관리만 '1 등' 처럼 단위를 붙인다 - 원본 표기를 유지한다. */
function gradeLabel(grade: string | null, palette: GradePalette) {
  if ((grade ?? '') === 'L') return 'LUCK';
  if (palette === 'prizeList') return grade ? `${grade} 등` : '-';
  return text(grade);
}

export function GradeBadge({
  grade,
  palette = 'orderDetail',
}: {
  grade: string | null;
  palette?: GradePalette;
}) {
  const key = grade ?? '';
  const content = gradeLabel(grade, palette);

  if (palette === 'prizeList') {
    const cls = key === 'L' ? 'l' : /^[1-7]$/.test(key) ? key : 'default';
    return <span className={`randombox-grade randombox-grade--${cls}`}>{content}</span>;
  }

  if (palette === 'customerHistory') {
    return (
      <span className="badge-grade" style={CUSTOMER_STYLE[key] ?? CUSTOMER_DEFAULT}>
        {content}
      </span>
    );
  }

  return (
    <span
      className="badge-grade"
      style={{ background: ORDER_DETAIL_GRADIENT[key] ?? ORDER_DETAIL_DEFAULT }}
    >
      {content}
    </span>
  );
}
